import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import config from '../config';
import './Auth.css';

export default function CharacterSelect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const token = params.get('token');
  const error = params.get('error');

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectError, setSelectError] = useState(null);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    // Décoder le token JWT (sans vérifier la signature côté client)
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(base64));
      setPayload(decoded);
    } catch (_) {
      navigate('/login?error=discord_error');
    }
  }, [token, navigate]);

  const handleSelect = async (characterId) => {
    setLoading(true);
    setSelectError(null);
    try {
      const res = await axios.post('/api/auth/select-character',
        { characterId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      login(res.data.token);
      navigate('/dashboard');
    } catch (e) {
      setSelectError(e.response?.data?.error || 'Erreur lors de la selection du personnage');
      setLoading(false);
    }
  };

  const noCharacter = error === 'no_license' || error === 'no_character' || payload?.noCharacter;

  if (!payload && !noCharacter) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <i className="fa-solid fa-spinner spinner" style={{ fontSize: 32, color: 'var(--primary)', marginBottom: 16 }} />
          <p style={{ color: 'var(--on-surface-variant)' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card auth-card-wide">
        {/* Header */}
        <div className="auth-logo">
          <img src="/logo.png" alt="Fleeca" className="auth-logo-img" onError={e => { e.target.style.display='none'; }} />
          <span className="auth-logo-text">Fleeca Bank</span>
        </div>

        {/* User info */}
        {payload && !payload.noCharacter && (
          <div className="discord-user-info">
            <img
              src={payload.avatar
                ? `https://cdn.discordapp.com/avatars/${payload.discordId}/${payload.avatar}.png?size=64`
                : '/logo.png'}
              alt="Avatar"
              className="discord-avatar"
              onError={e => { e.target.src = '/logo.png'; }}
            />
            <div>
              <p className="discord-username">{payload.username}</p>
              <span className="badge badge-primary" style={{ fontSize: 11 }}>
                <i className="fa-brands fa-discord" /> Verifie
              </span>
            </div>
          </div>
        )}

        {/* No character */}
        {noCharacter ? (
          <>
            <div className="auth-error-icon">
              <i className="fa-solid fa-building-columns" style={{ color: 'var(--outline)' }} />
            </div>
            <h2 className="auth-title">Aucun compte bancaire</h2>
            <p className="auth-desc" style={{ textAlign: 'center' }}>
              {error === 'no_license'
                ? 'Votre compte Discord n\'est pas lie a une license FiveM sur ce serveur. Connectez-vous au moins une fois en jeu.'
                : 'Aucun personnage avec un compte bancaire n\'est associe a votre compte Discord.'}
            </p>
            <div className="no-account-steps">
              <div className="no-account-step">
                <i className="fa-solid fa-gamepad" />
                <span>Connectez-vous au serveur FiveM Nexa RP</span>
              </div>
              <div className="no-account-step">
                <i className="fa-solid fa-user" />
                <span>Creez ou selectionnez un personnage</span>
              </div>
              <div className="no-account-step">
                <i className="fa-solid fa-building-columns" />
                <span>Revenez ensuite sur cette page</span>
              </div>
            </div>
            <a href="/" className="btn-secondary auth-btn">
              <i className="fa-solid fa-arrow-left" />
              Retour a l'accueil
            </a>
          </>
        ) : (
          <>
            <h2 className="auth-title" style={{ textAlign: 'center' }}>Choisissez votre personnage</h2>
            <p className="auth-desc" style={{ textAlign: 'center' }}>
              Selectionnez le personnage dont vous souhaitez gerer le compte bancaire.
            </p>

            {selectError && (
              <div className="auth-error-banner">
                <i className="fa-solid fa-circle-exclamation" />
                {selectError}
              </div>
            )}

            <div className="character-list">
              {(payload?.characters || []).map((char) => {
                const hasIban = !!char.iban;
                return (
                  <button
                    key={char.id}
                    className={`character-card${!hasIban ? ' character-card--locked' : ''}`}
                    onClick={() => hasIban && handleSelect(char.id)}
                    disabled={loading || !hasIban}
                    title={!hasIban ? 'Compte non ouvert' : ''}
                  >
                    <div className="character-avatar">
                      <i className={`fa-solid ${hasIban ? 'fa-user' : 'fa-lock'}`} />
                    </div>
                    <div className="character-info">
                      <span className="character-name">{char.name}</span>
                      {hasIban ? (
                        <span className="character-iban">{char.iban}</span>
                      ) : (
                        <span className="character-no-iban">
                          <i className="fa-solid fa-triangle-exclamation" /> Compte non ouvert — rendez-vous en banque en jeu
                        </span>
                      )}
                    </div>
                    <div className="character-balance">
                      {hasIban ? (
                        <>
                          <span className="balance-value">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(char.bankBalance || 0)}
                          </span>
                          <span className="balance-label">Solde</span>
                        </>
                      ) : (
                        <span className="balance-locked">Verrouillé</span>
                      )}
                    </div>
                    {hasIban && <i className="fa-solid fa-chevron-right character-arrow" />}
                  </button>
                );
              })}
            </div>

            {loading && (
              <div className="auth-loading-overlay">
                <i className="fa-solid fa-spinner spinner" style={{ fontSize: 28, color: 'var(--primary)' }} />
                <span>Connexion...</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
